import { Button, Group, Stack, TextInput } from '@mantine/core';
import { schemaResolver, useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { i18nKeys, useTranslation } from 'locales';

import {
  type UpdateStripeCardVariables,
  UpdateStripeCardVariablesSchema,
  useUpdateStripeCard,
} from '../api';

const groupCardNumber = (raw: string) =>
  raw
    .replace(/\D/g, '')
    .slice(0, 16)
    .replace(/(.{4})/g, '$1 ')
    .trim();

const formatExpiry = (raw: string) => {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

export const StripeCardModal = () => {
  const { t } = useTranslation();
  const { updateStripeCard, isUpdateStripeCardLoading } = useUpdateStripeCard();

  const form = useForm<UpdateStripeCardVariables>({
    initialValues: {
      bankAccountNumber: '',
      holderName: '',
      expiry: '',
      verificationNumbers: '',
    },
    validate: schemaResolver(UpdateStripeCardVariablesSchema, { sync: true }),
  });

  const handleSubmit = form.onSubmit(values => {
    updateStripeCard(
      { ...values, bankAccountNumber: values.bankAccountNumber.replace(/\s/g, '') },
      { onSuccess: () => modals.closeAll() },
    );
  });

  return (
    <form onSubmit={handleSubmit}>
      <Stack>
        <TextInput
          label={t(i18nKeys.FORM.STRIPE.CARD_NUMBER)}
          placeholder="4242 4242 4242 4242"
          required
          {...form.getInputProps('bankAccountNumber')}
          onChange={event =>
            form.setFieldValue('bankAccountNumber', groupCardNumber(event.currentTarget.value))
          }
        />
        <TextInput
          label={t(i18nKeys.FORM.STRIPE.FULL_NAME)}
          required
          {...form.getInputProps('holderName')}
        />
        <Group grow align="flex-start">
          <TextInput
            label={t(i18nKeys.FORM.STRIPE.EXPIRATION)}
            placeholder="MM/YY"
            required
            {...form.getInputProps('expiry')}
            onChange={event =>
              form.setFieldValue('expiry', formatExpiry(event.currentTarget.value))
            }
          />
          <TextInput
            label={t(i18nKeys.FORM.STRIPE.CVC)}
            placeholder="123"
            required
            {...form.getInputProps('verificationNumbers')}
            onChange={event =>
              form.setFieldValue(
                'verificationNumbers',
                event.currentTarget.value.replace(/\D/g, '').slice(0, 4),
              )
            }
          />
        </Group>
        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={() => modals.closeAll()}>
            {t(i18nKeys.CANCEL)}
          </Button>
          <Button type="submit" loading={isUpdateStripeCardLoading}>
            {t(i18nKeys.FORM.STRIPE.ADD_PAYMENT_METHOD)}
          </Button>
        </Group>
      </Stack>
    </form>
  );
};
